import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTheme } from '@mui/material';

import { MoreMenu } from '@zendesk/forethought-elements';
import CreateOrRenameAbTestDialog from './CreateOrRenameAbTestDialog';
import ConfirmModal from 'src/components/email-builder/components/ConfirmModal';
import {
  useDeleteAbTestConfigMutation,
  useDuplicateAbTestConfigMutation,
} from 'src/services/agent-ab-test/agentAbTestApi';
import {
  SolveABTestConfig,
  SolveABTestConfigSummary,
  SolveABTestStatus,
} from 'src/services/agent-ab-test/types';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';
import { Routes } from 'src/utils/enums';

type AbTestData = SolveABTestConfig | SolveABTestConfigSummary;

interface AbTestMoreMenuProps {
  abTest: AbTestData;
  /**
   * Whether to navigate to list page after delete.
   * This is typically true on the detail page.
   */
  navigateToListOnDelete?: boolean;
  /**
   * Callback when stop option is selected.
   */
  onStopClick?: () => void;
  /**
   * Whether to show "View current results" option for running/completed tests.
   * This is typically shown on the list page but not on the detail page.
   */
  showViewResultsOption?: boolean;
}

const AbTestMoreMenu = ({
  abTest,
  navigateToListOnDelete = false,
  onStopClick,
  showViewResultsOption = false,
}: AbTestMoreMenuProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('agent-ab-test');

  const [renamingAbTest, setRenamingAbTest] = useState<AbTestData>();
  const [deletingAbTestConfig, setDeletingAbTestConfig] =
    useState<AbTestData>();

  const cancelDelete = useCallback(() => {
    setDeletingAbTestConfig(undefined);
  }, []);

  const [deleteAbTestConfig, { isLoading: isDeleting }] =
    useDeleteAbTestConfigMutation({
      fixedCacheKey: 'delete-ab-test-config',
    });
  const [duplicateAbTestConfig] = useDuplicateAbTestConfigMutation({
    fixedCacheKey: 'duplicate-ab-test-config',
  });

  const getMenuOptions = useCallback(
    (status: SolveABTestStatus) => {
      if (status === 'draft') {
        return [
          { label: t('moreMenu.duplicate'), value: 'duplicate' },
          {
            color: theme.palette.error.main,
            label: t('moreMenu.delete'),
            value: 'delete',
          },
        ];
      }

      if (status === 'running') {
        const options = [];
        if (showViewResultsOption) {
          options.push({
            label: t('moreMenu.viewCurrentResults'),
            value: 'view-current-results',
          });
        }
        options.push({ label: t('moreMenu.rename'), value: 'rename' });
        options.push({ label: t('moreMenu.duplicate'), value: 'duplicate' });
        if (onStopClick) {
          options.push({
            color: theme.palette.error.main,
            label: t('moreMenu.stopAbTest'),
            value: 'stop',
          });
        }
        return options;
      }

      if (status === 'completed') {
        const options = [];
        if (showViewResultsOption) {
          options.push({
            label: t('moreMenu.viewCurrentResults'),
            value: 'view-current-results',
          });
        }
        options.push({ label: t('moreMenu.rename'), value: 'rename' });
        options.push({ label: t('moreMenu.duplicate'), value: 'duplicate' });
        options.push({
          color: theme.palette.error.main,
          label: t('moreMenu.delete'),
          value: 'delete',
        });
        return options;
      }

      return [
        { label: t('moreMenu.rename'), value: 'rename' },
        { label: t('moreMenu.duplicate'), value: 'duplicate' },
        {
          color: theme.palette.error.main,
          label: t('moreMenu.delete'),
          value: 'delete',
        },
      ];
    },
    [showViewResultsOption, t, theme.palette.error.main, onStopClick],
  );

  const handleMenuChange = useCallback(
    async (value: string) => {
      if (value === 'view-current-results') {
        navigate(
          `${Routes.AGENT_AB_TESTS}/${abTest.ab_test_config_id}?tab=results`,
        );
      } else if (value === 'rename') {
        setRenamingAbTest(abTest);
      } else if (value === 'duplicate') {
        try {
          const duplicatedAbTestConfig = await duplicateAbTestConfig(
            abTest.ab_test_config_id,
          ).unwrap();
          dispatch(
            setGlobalToastOptions({
              autoHideDuration: 5000,
              title: t('toast.copyCreated', {
                name: duplicatedAbTestConfig.name,
              }),
              variant: 'main',
            }),
          );
          navigate(
            `${Routes.AGENT_AB_TESTS}/${duplicatedAbTestConfig.ab_test_config_id}`,
          );
        } catch {
          dispatch(
            setGlobalToastOptions({
              autoHideDuration: 5000,
              title: t('toast.duplicateFailed', { name: abTest.name }),
              variant: 'danger',
            }),
          );
        }
      } else if (value === 'delete') {
        setDeletingAbTestConfig(abTest);
      } else if (value === 'stop') {
        onStopClick?.();
      }
    },
    [abTest, dispatch, duplicateAbTestConfig, navigate, onStopClick, t],
  );

  return (
    <>
      <MoreMenu
        onChange={handleMenuChange}
        options={getMenuOptions(abTest.status)}
      />
      {renamingAbTest && (
        <CreateOrRenameAbTestDialog
          abTestConfig={renamingAbTest}
          onClose={() => setRenamingAbTest(undefined)}
          open
        />
      )}
      {deletingAbTestConfig && (
        <ConfirmModal
          actions={[
            {
              onClick: cancelDelete,
              title: t('deleteDialog.cancelButton'),
            },
            {
              isLoading: isDeleting,
              onClick: async () => {
                try {
                  await deleteAbTestConfig(
                    deletingAbTestConfig.ab_test_config_id,
                  ).unwrap();

                  dispatch(
                    setGlobalToastOptions({
                      autoHideDuration: 5000,
                      title: t('toast.deleted', {
                        name: deletingAbTestConfig.name,
                      }),
                      variant: 'main',
                    }),
                  );

                  setDeletingAbTestConfig(undefined);
                  if (navigateToListOnDelete) {
                    navigate(Routes.AGENT_AB_TESTS);
                  }
                } catch {
                  dispatch(
                    setGlobalToastOptions({
                      autoHideDuration: 5000,
                      title: t('toast.deleteFailed', {
                        name: deletingAbTestConfig.name,
                      }),
                      variant: 'danger',
                    }),
                  );
                }
              },
              title: t('deleteDialog.confirmButton'),
            },
          ]}
          description={
            <>
              <p>
                <Trans
                  i18nKey='deleteDialog.body1'
                  ns='agent-ab-test'
                  values={{ name: deletingAbTestConfig.name }}
                />
                <br />
                {t('deleteDialog.body2')}
                <br />
                {t('deleteDialog.body3')}
              </p>
            </>
          }
          isOpen
          onClose={cancelDelete}
          title={t('deleteDialog.title')}
        />
      )}
    </>
  );
};

export default AbTestMoreMenu;
